import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Card, CardContent } from "../../components/ui/Card.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Button } from "../../components/ui/Button.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select.jsx";
import { DatePicker } from "../../components/ui/DatePicker.jsx";
import { useToast } from "../../hooks/useToast.js";
import { fetchActivityLogs } from "../../services/superAdminService.js";

const EVENT_TYPES = [
  "login",
  "lead_created",
  "lead_updated",
  "lead_deleted",
  "user_created",
  "company_created",
];

const EVENT_COLORS = {
  login: "bg-blue-50 text-blue-600",
  lead_created: "bg-green-50 text-green-600",
  lead_updated: "bg-yellow-50 text-yellow-700",
  lead_deleted: "bg-red-50 text-red-600",
  user_created: "bg-purple-50 text-purple-600",
  company_created: "bg-teal-50 text-teal-600",
};

export default function ActivityLogs() {
  const { toast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const loadLogs = async (p = page) => {
    setLoading(true);
    try {
      const res = await fetchActivityLogs({
        page: p,
        limit: 30,
        event_type: eventType,
        user_search: userSearch,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setLogs(res.data || []);
      setPagination(res.pagination || {});
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(1);
    setPage(1);
  }, [eventType, userSearch, dateFrom, dateTo]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-900">Activity Logs</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Filter by user name or email..."
            className="h-8 text-xs pl-9"
          />
        </div>
        <Select value={eventType} onValueChange={(v) => setEventType(v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 text-xs w-44">
            <SelectValue placeholder="All Events" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {EVENT_TYPES.map((e) => (
              <SelectItem key={e} value={e}>
                {e}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DatePicker
          value={dateFrom}
          onChange={setDateFrom}
          placeholder="From"
          className="h-8 text-xs w-40"
        />
        <DatePicker
          value={dateTo}
          onChange={setDateTo}
          placeholder="To"
          className="h-8 text-xs w-40"
        />
        {(eventType || userSearch || dateFrom || dateTo) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => {
              setEventType("");
              setUserSearch("");
              setDateFrom("");
              setDateTo("");
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <Card className="border-gray-100 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-500">Event</th>
                    <th className="text-left p-3 font-medium text-gray-500">User</th>
                    <th className="text-left p-3 font-medium text-gray-500">Company</th>
                    <th className="text-left p-3 font-medium text-gray-500">IP Address</th>
                    <th className="text-left p-3 font-medium text-gray-500">Details</th>
                    <th className="text-left p-3 font-medium text-gray-500">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    let meta = null;
                    try {
                      meta = typeof log.metadata === "string" ? JSON.parse(log.metadata) : log.metadata;
                    } catch {}

                    return (
                      <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              EVENT_COLORS[log.event_type] || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {log.event_type}
                          </span>
                        </td>
                        <td className="p-3">
                          <p className="text-gray-700">
                            {log.user_name
                              ? log.user_name
                              : meta?.source === "facebook_ads"
                                ? <span className="text-blue-600">Facebook Ads</span>
                                : "-"}
                          </p>
                          <p className="text-[10px] text-gray-400">{log.user_email}</p>
                        </td>
                        <td className="p-3 text-gray-600">{log.company_name || "-"}</td>
                        <td className="p-3 text-gray-500 font-mono">{log.ip_address || "-"}</td>
                        <td className="p-3 text-gray-500 max-w-[200px] truncate">
                          {meta
                            ? Object.entries(meta)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(", ")
                            : "-"}
                        </td>
                        <td className="p-3 text-gray-500 whitespace-nowrap">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-gray-400">
                        No logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Page {pagination.page} of {pagination.pages} ({pagination.total} logs)
          </p>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); loadLogs(page - 1); }}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={page >= pagination.pages}
              onClick={() => { setPage(page + 1); loadLogs(page + 1); }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
